import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { api } from "../../api";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import Modal from "../../components/common/Modal";
import { toast } from "react-toastify";
import { getStripeConfig, getEnvironmentConfig } from "../../utils/environment";
import { getSubscriptionDisplayCopy } from "../../config/ccwSubscriptionCopy";
import "./BookingExtras.css";

const stripePromise = loadStripe(getStripeConfig().publishableKey);

const BookingExtras = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingData, setBookingData] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [extras, setExtras] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extrasAlreadyAdded, setExtrasAlreadyAdded] = useState(false);
  const [bookingLocation, setBookingLocation] = useState(null);

  // Check if SMS offer was accepted from URL params
  const [smsOfferAccepted, setSmsOfferAccepted] = useState(null);

  // Check for SMS offer acceptance on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const smsAccepted = urlParams.get("smsOffer");

    if (smsAccepted === "true") {
      setSmsOfferAccepted(true);

      // Show success message (SMS was already sent in the modal)
      toast.success(
        "🎉 SMS offer sent! You'll receive a text message shortly with your free wash link.",
        {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } else if (smsAccepted === "false") {
      setSmsOfferAccepted(false);
      // No toast notification for declined offers
    }
  }, [location.search]);

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
  });

  // Modal helper functions
  const showModal = (
    title,
    message,
    type = "info",
    showCancel = false,
    onConfirm = null
  ) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm,
    });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Cart cookie cleared due to 404 error");
  };

  useEffect(() => {
    fetchBookingData();
  }, [bookingId]);

  const checkIfExtrasAlreadyAdded = (booking) => {
    // Check if booking notes contain "Extras:" indicating extras were already added
    const notes = booking?.posResponse?.BookingNotes || "";
    return notes.includes("Extras:");
  };

  const fetchBookingData = async () => {
    try {
      const response = await api(`booking-flow/booking/${bookingId}`, "GET");
      if (response.data.success) {
        setBookingData(response.data.booking);
        setCartData(response.data.cart);
        setBookingLocation(response.data.location || null);

        // Check if extras are already added
        const alreadyAdded = checkIfExtrasAlreadyAdded(response.data.booking);
        setExtrasAlreadyAdded(alreadyAdded);

        // Fetch extras and subscriptions if we have location data
        if (response.data.cart?.step1Data?.selectedLocationId) {
          await fetchExtrasAndSubscriptions(
            response.data.cart.step1Data.selectedLocationId
          );
        }
      } else {
        setError("Booking not found");
        // Clear cookie when booking is not found
        clearCartCookie();
      }
    } catch (err) {
      console.error("Error fetching booking data:", err);
      // Check if it's a 404 error and clear cookie
      if (err.response?.status === 404) {
        setError("Booking not found");
        clearCartCookie();
      } else {
        setError("Failed to load booking data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExtrasAndSubscriptions = async (locationId) => {
    try {
      // Fetch extras for the location
      const extrasResponse = await api(
        `booking-flow/location/${locationId}/extras`,
        "GET"
      );
      if (extrasResponse.data.success) {
        setExtras(extrasResponse.data.extras || []);
      }

      // Fetch subscriptions
      const subscriptionsResponse = await api(
        "booking-flow/subscriptions",
        "GET"
      );
      if (subscriptionsResponse.data.success) {
        setSubscriptions(subscriptionsResponse.data.subscriptions || []);
      }
    } catch (err) {
      console.error("Error fetching extras/subscriptions:", err);
    }
  };

  const handleExtraSelect = (extraId) => {
    setSelectedExtras((prev) => {
      if (prev.includes(extraId)) {
        return prev.filter((id) => id !== extraId);
      } else {
        return [...prev, extraId];
      }
    });
  };

  const navigateToConfirmation = () => {
    const urlParams = new URLSearchParams(location.search);
    const smsOfferParam = urlParams.get("smsOffer");
    const successUrl = smsOfferParam
      ? `/booking-success/${bookingId}?smsOffer=${smsOfferParam}`
      : `/booking-success/${bookingId}`;

    clearCartCookie();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(successUrl);
  };

  const handleContinue = async () => {
    // If extras are already added, go directly to success screen
    if (extrasAlreadyAdded) {
      navigateToConfirmation();
      return;
    }

    if (selectedExtras.length === 0) {
      // No extras selected, go directly to success screen
      navigateToConfirmation();
      return;
    }

    // Show confirmation modal
    const extrasList = selectedExtras
      .map((id) => {
        const extra = extras.find((e) => e._id === id);
        return extra ? `- ${extra.name}` : "";
      })
      .join("\n");

    showModal(
      "Confirm Extras",
      `The selected extras will be added to your booking:\n\n${extrasList}`,
      "info",
      true, // show cancel button
      async () => {
        // This is the onConfirm callback
        try {
          // Add extras to booking
          await api(`booking-flow/booking/${bookingId}/add-extras`, "POST", {
            extras: selectedExtras,
          });

          navigateToConfirmation();
        } catch (err) {
          console.error("Error adding extras:", err);
          // Clear cookie if it's a 404 error (booking not found)
          if (err.response?.status === 404) {
            clearCartCookie();
          }
          showModal(
            "Error",
            "Failed to add extras to booking. Please try again.",
            "error"
          );
        }
      }
    );
  };

  const generateSubscriptionUrl = (subscription) => {
    // Link to subscriptions landing page instead of directly to P1
    // The subscriptions page will handle generating the P1 signup URL with all parameters
    const baseUrl = "/subscriptions";
    const params = new URLSearchParams();

    // Add booking ID if available (from route params)
    if (bookingId) {
      params.append("booking", bookingId);
      // Add return URL to go back to extras page after viewing subscriptions
      params.append("returnUrl", `/booking-extras/${bookingId}`);
    }

    // Add customer details as URL parameters if available (will be passed through to P1)
    if (cartData?.step3Data || cartData?.step1Data) {
      const customerData = cartData.step3Data || cartData.step1Data;

      if (customerData.email) params.append("email", customerData.email);
      if (customerData.phone) params.append("phone", customerData.phone);
      if (customerData.firstName)
        params.append("firstName", customerData.firstName);
      if (customerData.lastName)
        params.append("lastName", customerData.lastName);
    }

    const urlSiteId = new URLSearchParams(location.search).get("siteId");
    const siteId =
      urlSiteId ||
      bookingData?.idLocation?.posId ||
      bookingLocation?.posId ||
      cartData?.step1Data?.selectedLocation?.posId;
    if (siteId) {
      params.append("siteId", String(siteId));
    }

    return `${baseUrl}?${params.toString()}`;
  };

  const formatPrice = (s) => {
    const price =
      s?.monthlyPrice ??
      s?.lockedPrice ??
      s?.priceMonthly ??
      (typeof s?.price === "object" ? s?.price?.monthly : undefined);
    if (price == null) return "—";
    try {
      const n = Number(price);
      if (Number.isFinite(n)) return `$${n.toFixed(2)}/month`;
      return `$${price}/month`;
    } catch (e) {
      return `$${price}/month`;
    }
  };

  if (loading) {
    return (
      <div className="booking-extras-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-extras-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/")}>Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-extras-container">
      <div className="booking-extras-header">
        <img src={ConciergeLogo} alt="Concierge Car Wash" className="logo" />
        <h1>Before you arrive please select your extras!</h1>
        <p className="subtitle">
          We'll add these to your booking - no upfront payment required
        </p>

        {/* SMS Offer Status Display */}
        {smsOfferAccepted === true && (
          <div className="sms-offer-status accepted">
            <div className="status-icon">🎉</div>
            <div className="status-text">
              <strong>Free Wash Offer Sent!</strong>
              <p>
                Check your phone for a text message with your free wash link.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="extras-section">
        <h2>Add Extras</h2>
        {extrasAlreadyAdded ? (
          <div className="extras-already-added">
            <p>✅ Extras have already been added to this booking.</p>
            <p>
              If you need to make changes, please use the "Manage Booking"
              button on the confirmation page.
            </p>
          </div>
        ) : extras.length > 0 ? (
          <div className="extras-grid">
            {extras.map((extra) => (
              <div
                key={extra._id}
                className={`extra-card ${
                  selectedExtras.includes(extra._id) ? "selected" : ""
                }`}
                onClick={() => handleExtraSelect(extra._id)}
              >
                <div className="card-content">
                  <h3>{extra.name}</h3>
                  <div className="duration">
                    <span className="clock-icon">🕐</span>
                    <span>From {extra.duration || "25mins"}</span>
                  </div>
                </div>
                <div className="card-right">
                  <span className="price">From ${extra.price}.00</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>No extras available for this location.</p>
        )}
      </div>

      <div className="subscriptions-section">
        <h2>Subscriptions</h2>
        {(() => {
          const allowedKeywords = ["shine", "glow", "sparkle"];
          const filtered = (subscriptions || []).filter((s) => {
            const name = (s?.name || "").toLowerCase();
            return allowedKeywords.some((k) => name.includes(k));
          });

          // Sort subscriptions by price (Sparkle $69.95 → Shine $109.95 → Glow $269.95)
          const sorted = filtered.sort((a, b) => {
            const priceA = a?.monthlyPrice ?? a?.lockedPrice ?? 0;
            const priceB = b?.monthlyPrice ?? b?.lockedPrice ?? 0;
            return priceA - priceB;
          });

          if (!sorted.length) return <p>No subscriptions available.</p>;

          return (
            <div className="extras-grid">
              {sorted.map((subscription) => {
                const display = getSubscriptionDisplayCopy(subscription);
                return (
                <div key={subscription._id} className="subscription-card">
                  <div className="card-content">
                    <h3>{subscription.name}</h3>
                    {display.description && (
                      <p className="product-description">
                        {display.description}
                      </p>
                    )}
                    <p className="description powered-by">Powered by P1</p>
                  </div>
                  <div className="card-right">
                    <p className="price">{formatPrice(subscription)}</p>
                    <a
                      href={generateSubscriptionUrl(subscription)}
                      className="external-link-btn pill"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <div className="gift-card-section">
        <h2>Gift Cards</h2>
        <div className="extras-grid">
          <div className="gift-card">
            <div className="card-content">
              <h3>Gift card</h3>
              <p className="price">From $50</p>
              <p className="description">Perfect gift for car lovers</p>
            </div>
            <a
              href="https://conciergecarwash.com.au/purchase-concierge-car-wash-gift-cards/"
              target="_blank"
              rel="noopener noreferrer"
              className="external-link-btn"
            >
              Purchase Gift Card
            </a>
          </div>
        </div>
      </div>

      <div className="continue-section">
        <div className="button-group">
          <button
            className="skip-btn"
            onClick={navigateToConfirmation}
          >
            Skip
          </button>
          <button className="continue-btn" onClick={handleContinue}>
            {extrasAlreadyAdded
              ? "Continue"
              : selectedExtras.length > 0
              ? "Confirm Extras"
              : "Continue"}
          </button>
        </div>
      </div>

      {/* Alert Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        showCancel={modal.showCancel}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
};

export default BookingExtras;
